import { DocumentPickerAsset } from "expo-document-picker";
import { ImagePickerAsset } from "expo-image-picker";

export type visibility = "Public" | "Group";
export enum eventRegistrationPolicy {
  NONE = "None",
  REQUIRED = "Required",
  INVITE_ONLY = "Invite",
}

// CR-B2-10: online vs in-person. Schema field blocked (open item 5) — client UI only.
export enum EventType {
  IN_PERSON = "InPerson",
  ONLINE = "Online",
}

const looksLikeUrl = (value?: string | null): boolean =>
  !!value && /^https?:\/\//i.test(value.trim());

/**
 * The meeting URL of an online event, or null when it is not an online event.
 *
 * Three sources, in order, because the data has three generations:
 *   1. `meetingLink` — the real schema field (added 2026-08-03). Preferred.
 *   2. `eventType === Online` + a URL-shaped `locationName` — events created while
 *      `meetingLink` was schema-blocked stored the URL in `locationName`.
 *   3. a URL-shaped `locationName` alone — `eventType` is NOT a schema field, so it
 *      comes back undefined on every read; without this an older online event is
 *      indistinguishable from an in-person one and its URL would render as a venue.
 */
export const getMeetingUrl = (event: {
  meetingLink?: string;
  locationName?: string;
  eventType?: EventType;
}): string | null => {
  const link = event.meetingLink?.trim();
  if (link) return link;
  if (looksLikeUrl(event.locationName)) return event.locationName!.trim();
  return null;
};

/** True when the event is online — i.e. it resolves to a meeting URL. */
export const isOnlineEvent = (event: {
  meetingLink?: string;
  locationName?: string;
  eventType?: EventType;
}): boolean => event.eventType === EventType.ONLINE || !!getMeetingUrl(event);

export type InsertEventInput = {
  title: string;
  description: string;
  singleDay: boolean;
  startTime: string;
  endTime: string;
  locationName: string;
  locationLat: number;
  locationLng: number;
  categoryIds: string[];
  coverImage?: string;
  visibility: visibility;
  registrationPolicy: eventRegistrationPolicy;
  capacity?: number | null;
  groupIds?: string[];
  attachmentUrls?: string[];
  Tags: string[];
  // `meetingLink` was added to the Data Gateway schema on 2026-08-03, so it is
  // sent and persisted. `eventType` was NOT — sending it would fail schema
  // validation and break every event mutation. Online-ness is therefore derived
  // from `meetingLink` being present, which is the same signal by another name.
  meetingLink?: string;
  // eventType?: EventType;  ← still absent from the schema. Do not send.
};

export type InsertEventResponse = {
  insertevent: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};

export type Step1FormValues = {
  title: string;
  description: string;
  singleDay: boolean;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  locationName: string;
  locationLat: number;
  locationLng: number;
  categoryIds: string[];
  coverImage?: ImagePickerAsset | null;
  attachments?: DocumentPickerAsset[];
  existingAttachments?: string[];
  // CR-B2-10: client-only until Data Gateway schema sync (open item 5)
  eventType?: EventType;
  meetingLink?: string;
};

export type Step2FormValues = {
  visibility: visibility;
  registrationPolicy: eventRegistrationPolicy;
  capacity?: number | null;
  groupIds?: string[];
};

export type EventOrganizerSnapshot = {
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
};
export type EventParticipantSnapshot = {
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
};

// Listing
export type EventItem = {
  ItemId: string;
  CreatedDate: string;
  CreatedBy: string;
  LastUpdatedDate: string;
  LastUpdatedBy: string;
  Language: string;
  OrganizationId: string;
  Tags: string[];
  DeletedDate: string | null;
  title: string;
  description: string;
  singleDay: boolean;
  startTime: string;
  endTime: string;
  locationName: string;
  locationLat: number;
  locationLng: number;
  categoryIds: string[];
  coverImage: string | null;
  visibility: string;
  registrationPolicy: eventRegistrationPolicy;
  capacity: number;
  attachmentUrls: string[];
  eventOrganizerSnapshot: EventOrganizerSnapshot | null;
  groupIds: string[];
  // CR-B2-10: schema-blocked — will be undefined until Data Gateway sync (open item 5)
  eventType?: EventType;
  meetingLink?: string;
};

export type GetEventsResponse = {
  getevents: {
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    items: EventItem[];
  };
};

export type GetEventsInput = {
  where: Record<string, unknown>;
  order: readonly { field: string; direction: string }[];
  pageNo: number;
  pageSize: number;
};

export type UpdateEventResponse = {
  updateevent: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};

export type DeleteEventResponse = {
  deleteevent: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};

export type AttachmentItem =
  | { type: "existing"; url: string }
  | { type: "new"; file: DocumentPickerAsset };

// RSVP
export enum RSVPStatus {
  GOING = "Going",
  WAITING = "Waiting",
  // CR-B2-08: Declined is distinct from no-RSVP-row (never responded) and deleted row (joined-then-left).
  // Schema change blocked (open item 5); inserts with this status may fail validation server-side.
  DECLINED = "Declined",
}

export type RSVPParticipantSnapshot = {
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
};

export type RSVPItem = {
  ItemId: string;
  CreatedDate: string;
  CreatedBy: string;
  eventId: string;
  status: RSVPStatus;
  eventParticipantSnapshot: RSVPParticipantSnapshot;
  /**
   * Names of guests this attendee is bringing. The count is `guestNames.length`
   * — a separate count field would be a second source of truth that can drift.
   *
   * ⚠ Guests deliberately do NOT consume capacity (see `use-event-rsvp.hook`);
   * capacity counts registered attendees only. Totals that include guests are
   * labelled as such wherever they are shown.
   *
   * Schema field required on the RSVP entity: `guestNames: [String]`.
   */
  guestNames?: string[];
};
export type GetRSVPsResponse = {
  getRSVPs: {
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    items: RSVPItem[];
  };
};

export type InsertRSVPResponse = {
  insertRSVP: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};

export type UpdateRSVPResponse = {
  updateRSVP: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};

export type DeleteRSVPResponse = {
  deleteRSVP: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};

// Event Gallery
export type EventGalleryItem = {
  ItemId: string;
  CreatedDate: string;
  CreatedBy: string;
  LastUpdatedDate: string;
  LastUpdatedBy: string;
  Language: string | null;
  OrganizationId: string;
  Tags: string[];
  DeletedDate: string | null;
  eventId: string;
  imageUrl: string;
};

export type GetEventGalleryResponse = {
  geteventgallerys: {
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    items: EventGalleryItem[];
  };
};

export type InsertEventGalleryResponse = {
  inserteventgallery: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};

export type DeleteEventGalleryResponse = {
  deleteeventgallery: {
    acknowledged: boolean;
    totalImpactedData: number;
    itemId: string;
  };
};
